import classNames from 'classnames';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { Mode } from '~/types/client';
import { ONBOARDING_GAME_ID } from '~/config/global.config';
import { logEvent } from '~/config/firebase.config';
import { useClients, useSyncClient } from '~/hooks/models/useClient';
import { useLocalStorageSelect } from '~/hooks/useLocalStorageSelect';
import { useAuth } from '~/services/auth';
import { useSeasonalTheme } from '~/hooks/useSeasonalTheme';
import { isLikelyMobile } from '~/utils/isLikelyMobile';
import { isLikelyPhone } from '~/utils/isLikelyPhone';

import { Navbar } from '~/components/navigation/Navbar';
import { useBrandTheme, useClientBranding } from '~/hooks/useClientBranding';
import { SeasonalHomeDecor, SeasonalPill, seasonalContainerClass } from '~/components/seasonal';
import { usePauseActive } from '~/hooks/usePauseActive';
import { useVenueTitle } from '~/hooks/useVenueTitle';

import { LoadingPage } from '~/pages/LoadingPage';
import { HomeAnimation } from '~/pages/HomeAnimation';
import { HomeBox } from '~/pages/HomeBox';
import { OnboardingBanner } from '~/pages/Onboarding';

import { PhoneHome } from './PhoneHome';
import styles from './HomeContent.module.css';

/**
 * « Afficher le jeu sur ce téléphone » choisi pour CET onglet (sessionStorage) :
 * il n'y a plus de bandeau « Retour » (v2), donc un choix fait par erreur ne doit
 * pas coller au téléphone — fermer l'onglet ramène à l'accueil téléphone.
 */
const PHONE_SHOW_TV_KEY = 'phoneHomeShowTv';

// Fait main : si le stockage est bloqué (Safari privé, cf gotcha #176), le choix
// vaut quand même pour cet affichage.
function readShowTv() {
    try {
        return sessionStorage.getItem(PHONE_SHOW_TV_KEY) === '1';
    } catch {
        return false;
    }
}

function rememberShowTv() {
    try {
        sessionStorage.setItem(PHONE_SHOW_TV_KEY, '1');
    } catch {
        /* stockage bloqué : le choix vaut pour cet affichage seulement. */
    }
}

export function HomeContent({ clientIDs }: { clientIDs: string[] }) {
    const { auth } = useAuth();

    const { t, i18n } = useTranslation();
    const { currentValue, onChange } = useLocalStorageSelect('clientID', clientIDs.sort());
    const clients = useClients(clientIDs);
    const client = useSyncClient(currentValue);
    // Formule Pro : logo + couleurs de l'établissement affiché (services/branding/clientBranding.ts).
    const branding = useClientBranding(currentValue);
    useBrandTheme(branding);

    const spaces = useMemo(
        () => clients?.map((cl) => ({ id: cl.id, name: cl.name })).sort((a, b) => a.name.localeCompare(b.name)) || [],
        [clients]
    );

    useEffect(() => {
        logEvent(currentValue === 'demo' ? 'demo_home' : `${currentValue}_home`);
    }, [currentValue]);

    useVenueTitle(currentValue, client);

    useEffect(() => {
        if (client?.language) {
            i18n.changeLanguage(client?.language);
        }
    }, [client?.language]);

    // Tutoriel d'onboarding (mode anim uniquement, jamais pour le compte demo) :
    // déclenché à l'arrivée depuis la page success (?onboarding=1) ou si le
    // client porte le flag onboardingCompleted === false (posé à la création —
    // strictement false : un client historique sans le champ ne le voit jamais).
    // Sur téléphone/tablette : pas de tuto (il faudrait scanner son propre
    // écran) — le flag reste non-true, le tuto se lancera à la première visite
    // depuis un ordinateur. À la place : la passerelle « continue sur ton
    // ordinateur » (/welcome).
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const fromSignup = searchParams.get('onboarding') === '1';
    const wantsOnboarding =
        !!client &&
        client.mode !== Mode.Auto &&
        !client.isDemo() &&
        // En pause : le tutoriel lancerait un jeu, ScreenApp renverrait au menu → boucle.
        !client.isPaused &&
        (fromSignup || client.onboardingCompleted === false);
    const onMobile = isLikelyMobile();
    const needsOnboarding = wantsOnboarding && !onMobile;
    const needsBridge = wantsOnboarding && onMobile && fromSignup;
    // Bar actif ouvert sur téléphone : accueil téléphone (la TV, c'est un
    // ordinateur) au lieu de la page TV, sauf si l'utilisateur a choisi de voir
    // la page TV quand même. Résilié / en pause : page TV inchangée (calques de
    // réabonnement / de pause).
    const [showTvOnPhone, setShowTvOnPhone] = useState(readShowTv);
    const showTvHere = () => {
        rememberShowTv();
        setShowTvOnPhone(true);
    };
    const pausedNow = usePauseActive(client?.pause);
    const phoneEligible =
        isLikelyPhone() && !!client && client.mode !== Mode.Auto && !client.isDemo() && !client.disabled && !pausedNow;
    const showPhoneHome = phoneEligible && !showTvOnPhone;
    // Visites mobiles ultérieures (hors arrivée signup) tant que le tuto
    // n'est pas fait : bannière « recevoir le lien » plutôt que la passerelle.
    // Inutile sur l'accueil téléphone, qui porte déjà le même lien.
    const showBanner = wantsOnboarding && onMobile && !fromSignup && !showPhoneHome;

    useEffect(() => {
        // Mesure : inscriptions finalisées sur mobile (tuto différé).
        if (needsBridge && client) {
            logEvent('onboarding_deferred_mobile', client.id);
            navigate('/welcome', { replace: true });
        }
    }, [needsBridge, client?.id]);

    useEffect(() => {
        if (!needsOnboarding || !client) return;
        logEvent('onboarding_started', client.id);
        navigate(`/app/${client.id}?game=${ONBOARDING_GAME_ID}&onboarding=1`, { replace: true });
    }, [needsOnboarding, client?.id]);

    // Thème de saison (Halloween) : bars en mode animé seulement, par date, coupable à distance.
    // Pas sur un compte résilié ou en pause (l'annonce « prêts à jouer » surplomberait le calque).
    const seasonTheme = useSeasonalTheme(client?.id, client?.mode);
    const seasonal = client && !client.disabled && !pausedNow && !showPhoneHome ? seasonTheme : null;
    useEffect(() => {
        if (seasonal && client) logEvent('seasonal_theme_home', client.id, { clientID: client.id, theme: seasonal });
    }, [seasonal, client?.id]);

    if (!client || needsOnboarding || needsBridge) {
        return <LoadingPage>{t('home.loadingClient')}</LoadingPage>;
    }

    return (
        <div className={classNames(styles.container, seasonalContainerClass(seasonal))}>
            <Navbar
                clientID={currentValue}
                clientName={client?.name}
                spaces={spaces}
                currentSpace={currentValue}
                onSwitchSpace={(id) => onChange({ value: id })}
                center={seasonal && client.mode !== Mode.Auto ? <SeasonalPill id={seasonal} /> : undefined}
                brandLogo={branding?.logo}
            />
            {showBanner && <OnboardingBanner clientID={client.id} email={auth?.email || ''} />}
            {showPhoneHome && <PhoneHome clientID={client.id} email={auth?.email || ''} onShowTv={showTvHere} />}
            {!showPhoneHome && client?.mode === Mode.Auto && <HomeBox client={client} />}
            {!showPhoneHome && client?.mode !== Mode.Auto && <HomeAnimation client={client} seasonal={seasonal} />}
            {seasonal && client.mode !== Mode.Auto && <SeasonalHomeDecor />}
        </div>
    );
}
